import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { environment } from '@env/environment';
import { getValuesFromPath } from '@upr-web-primer/app-utils';
import { DalService } from '@upr-web-primer/dal';
import { MsGuestsService } from '@upr-web-primer/ms-guests';
import { NotificationService } from '@upr-web-primer/notification';
import { OidcService } from '@upr-web-primer/oidc';
import { PageLabelsResolver } from '@upr-web-primer/page-labels';
import {
  LocalStorageService,
  SessionStorageService,
} from '@upr-web-primer/storage';
import { UdxSpinnerService } from '@upr-web-primer/udx-spinner';
import { get, isEmpty } from '@upr-web-primer/udx-utils';
import { WINDOW } from '@upr-web-primer/window';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { ApplicationDataService } from './services/application-data.service';
import { CommonUtilityService } from './services/common-utility.service';
import { PreferenceMergeService } from './services/preference-merge.service';

@Injectable({
  providedIn: 'root',
})
export class OidcLoginGuard {
  isLoggedIn: any;
  pageLabels: any;

  constructor(
    private readonly common: CommonUtilityService,
    private readonly localStorage: LocalStorageService,
    @Inject('AppRoutes') public appRoutes: any,
    @Inject('DefaultValues') private readonly defaultValues: any,
    private readonly dalService: DalService,
    private readonly sessionStorage: SessionStorageService,
    @Inject(WINDOW) private readonly window: any,
    private readonly applicationDataService: ApplicationDataService,
    private readonly notification: NotificationService,
    private readonly spinner: UdxSpinnerService,
    private readonly pageLabelsService: PageLabelsResolver,
    private readonly cookieService: CookieService,
    private readonly oidcService: OidcService,
    private readonly msGuests: MsGuestsService,
    private readonly preferenceMergeService: PreferenceMergeService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    // Initialize page labels if not already loaded
    this.initializePageLabels();

    return this.oidcLogin(route);
  }

  private initializePageLabels(): void {
    if (!this.pageLabels) {
      this.pageLabelsService?.get().then((pageLabels: any) => {
        this.pageLabels = pageLabels;
      });
    }
  }

  async oidcLogin(route: ActivatedRouteSnapshot): Promise<boolean> {
    const fragments = getValuesFromPath(this.window.location.href);
    const isAccountDelete =
      this.localStorage.getItem('isAccountDelete') === 'true';
    if (isAccountDelete) {
      this.localStorage.setItem('isAccountDelete', 'false');
      const redirectUrl = this.common.getOidcRedirectionUrl();
      this.common?.redirectToOidcAppEndSession(redirectUrl);
      return false;
    }

    // Handle sign-off scenario
    const isSignOff =
      (fragments?.['signoff'] || route?.queryParams?.['signoff']) === 'true';
    if (isSignOff) {
      this.spinner.show('logout');
      try {
        await this.msGuests.account.logout();
      } catch (_error) {
        // Sign-off logout error handled silently
      }
    }

    // If already logged in, go to profile page
    if (this.msGuests.helper.isAuth() && !isAccountDelete) {
      this.common.changeRoute(this.appRoutes.DASHBOARD);
      return true;
    }

    const oidcRedirectEnabled = get(
      this.defaultValues,
      'OIDC_REDIRECT_ENABLED',
    );

    // If OIDC redirect is disabled or account is deleted, allow access

    if (!oidcRedirectEnabled) {
      return true;
    }

    // Check for OIDC code and fetch tokens
    const isEmptyQuery = isEmpty(route.queryParams);

    const auth_code = isEmptyQuery
      ? fragments['code']
      : get(route.queryParams, 'code');
    if (auth_code) {
      // User came with code from OIDC provider, so fetch tokens
      const response = await this.oidcService.getTokensByCode(
        auth_code,
        this.common.getOidcRedirectionUrl(),
      );

      if (response.id_token && response.access_token && response.expires_in) {
        this.dalService.setOidcLoginSession(
          response.access_token,
          response.refresh_token || '',
          response.id_token,
          response.expires_in,
        );
        this.localStorage.setItem('OIDC_ACCESS_TOKEN', response.access_token);
        this.localStorage.setItem('OIDC_ID_TOKEN', response.id_token);
        this.localStorage.setItem('OIDC_EXPIRES_IN', response.expires_in);
        this.sessionStorage.setItem('OIDC_ID_TOKEN', response.id_token);
        this.sessionStorage.setItem('OIDC_EXPIRES_IN', response.expires_in);
        this.sessionStorage.setItem('OIDC_ACCESS_TOKEN', response.access_token);

        const guestId = this.localStorage.getItem('OAUTH_LOGIN_GID');
        this.localStorage.setItem('oAuthLoginUserName', guestId);
        this.localStorage.setItem('LOGGEDIN_GUEST_ID', guestId);
        this.localStorage.setItem('IS_SIGN_IN', 'true');
        this.sessionStorage.setItem('IS_SIGN_IN', 'true');
        if (environment?.showProfileUpdates) {
          this.setCookieInfo(
            response.access_token,
            response.expires_in,
            guestId,
            response.id_token,
          );
        } else {
          this.setCookieInfo(
            response.access_token,
            response.expires_in,
            guestId,
          );
        }

        this.common.setIdleTimeout(response.expires_in);
        this.preferenceMergeService.mergeUnauthenticatedFavorites(guestId);
        this.common.changeRoute(this.appRoutes.DASHBOARD);
        return true;
      }
    }

    const wtsAccessToken = this.cookieService?.get('access_token');
    const wtsAccessCommerceToken = this.cookieService?.get('commerceTokenInfo');
    const wtsAccessExpiresIn = this.cookieService?.get('expires_in');
    const refresh_token = this.localStorage.getItem('oAuthRefreshToken');
    const guestId = this.msGuests.helper.getGuestId();
    if (wtsAccessToken && wtsAccessCommerceToken && wtsAccessExpiresIn) {
      this.localStorage.setItem('IS_SIGN_IN', 'true');
      this.sessionStorage.setItem('IS_SIGN_IN', 'true');

      if (!environment?.showProfileUpdates) {
        this.localStorage.setItem('oAuthToken', wtsAccessToken);
        this.dalService.setOAuthTokens(
          true,
          wtsAccessToken,
          refresh_token,
          Number(wtsAccessExpiresIn),
          Number(wtsAccessExpiresIn),
        );
      }
      this.preferenceMergeService.mergeUnauthenticatedFavorites(guestId);
      this.common.changeRoute(this.appRoutes.DASHBOARD);
      return Promise.resolve(true);
    }

    // Redirect to OIDC provider if no tokens or code are found
    const redirectUrl = this.common.getOidcRedirectionUrl();
    const locationPort = get(this.window, 'location.port', '');
    const locationHost = get(this.window, 'location.host', '');
    if (
      locationPort &&
      locationPort !== '4202' &&
      !locationHost.includes('web-local') &&
      !locationHost.includes('localhost')
    ) {
      return true;
    }
    this.common.redirectToOidcApp(redirectUrl);
    return true;
  }

  getValueFromFragment(key: string, route: ActivatedRouteSnapshot): string {
    const fragment = get(route, 'fragment', '');
    if (!fragment) {
      return '';
    }
    const fragmentList: any = fragment
      .split('&')
      .reduce((prev: any, item: any) => {
        return Object.assign(
          { [item.split('=')[0]]: item.split('=')[1] },
          prev,
        );
      }, {});
    return fragmentList && fragmentList[key];
  }

  setCookieInfo(
    access_token: string,
    expires_in: number,
    guestId: string,
    id_token?: string,
  ): void {
    if (id_token) {
      this.applicationDataService.setApplicationCookieInfo(
        'id_token',
        id_token,
      );
    }

    this.applicationDataService.deleteCookie('logout');
    this.applicationDataService.setApplicationCookieInfo(
      'access_token',
      access_token,
    );
    this.applicationDataService.setApplicationCookieInfo(
      'expires_in',
      `${expires_in}`,
    );
    this.applicationDataService.setApplicationCookieInfo('guestId', guestId);
  }

  commerceAuthentication(): any {
    this.spinner.show();

    this.common?.getB2COAuthToken()?.subscribe((authData: any) => {
      this.localStorage.setItem('authObj', JSON.stringify(authData));

      if (authData) {
        this.common.postauthLogin().subscribe({
          next: (loginAPIResponse: any) => {
            this.spinner.hide();
            this.localStorage.setItem(
              'loginAuthToken',
              JSON.stringify(loginAPIResponse),
            );
            this.applicationDataService.setApplicationCookieInfo(
              'commerceTokenInfo',
              JSON.stringify(loginAPIResponse),
            );
            if (environment?.showProfileUpdates) {
              this.localStorage.removeItem('dashboard_version');
            }
            this.common.changeRoute(this.appRoutes.DASHBOARD);
          },
          error: (errors: any) => {
            const errorCode = errors.error?.errorMessage
              ? errors.error?.errorMessage
              : errors.error.errors[0]?.message;
            this.notification?.show(errorCode);
            this.common.eventDDlTrigger(errorCode);
          },
        });
      }
    });
    return Promise.resolve(true);
  }
}
